import {
  normalizeSystemGroupingPreference,
  parseJson,
  type SystemGroupingPreferenceValue,
} from './pcfSettingsSerialization';

export const MISSING_GROUPING_PARAMETER_MESSAGE =
  'Choose a grouping parameter in Export settings before exporting by parameter.';

interface PcfGroupingConfiguration {
  groupingMode: SystemGroupingPreferenceValue;
  parameterName: string;
}

export function getPcfGroupingConfigurationError(
  groupingMode: string,
  parameterName: string
): string | null {
  const normalizedMode = groupingMode.trim().toLowerCase();
  const usesParameter = normalizedMode === 'parameter' || normalizedMode === 'byparameter';
  return usesParameter && !parameterName.trim()
    ? MISSING_GROUPING_PARAMETER_MESSAGE
    : null;
}

export function resolvePcfGroupingConfiguration(
  advancedSettings: string
): PcfGroupingConfiguration {
  const wrapper = parseJson<Record<string, unknown>>(advancedSettings, {});
  const base = typeof wrapper.base === 'string'
    ? parseJson<Record<string, unknown>>(wrapper.base, {})
    : wrapper;
  const grouping = base.GroupingParameter ?? base.groupingParameter;
  const parameterName = grouping && typeof grouping === 'object'
    ? String(
        (grouping as Record<string, unknown>).ParameterName
          ?? (grouping as Record<string, unknown>).parameterName
          ?? ''
      ).trim()
    : '';

  return {
    groupingMode: normalizeSystemGroupingPreference(
      base.SystemGroupingPreference ?? base.systemGroupingPreference
    ),
    parameterName,
  };
}

export interface PcfGroupingOverlay {
  groupingMode: SystemGroupingPreferenceValue;
  parameterName: string;
  source: 'Element' | 'Type';
}

// The calculate request takes its grouping enum from the live session while the
// settings payload historically came from the stored profile verbatim — an
// unsaved in-session parameter choice then reached the plugin as BY_PARAMETER
// with no GroupingParameter and every element was dropped from grouping. The
// overlay makes the payload match what the export dialog shows; non-parameter
// modes drop the section just like the DTO builder does on save.
export function overlayPcfGroupingConfiguration(
  advancedSettings: string,
  overlay: PcfGroupingOverlay
): string {
  const wrapper = parseJson<Record<string, unknown>>(advancedSettings, {});
  const hasWrapperBase = typeof wrapper.base === 'string';
  const base = hasWrapperBase
    ? parseJson<Record<string, unknown>>(wrapper.base as string, {})
    : { ...wrapper };

  delete base.systemGroupingPreference;
  base.SystemGroupingPreference = overlay.groupingMode;
  delete base.groupingParameter;
  if (overlay.groupingMode === 'ByParameter') {
    base.GroupingParameter = {
      ParameterName: overlay.parameterName.trim(),
      Source: overlay.source === 'Type' ? 'Type' : 'Element',
    };
  } else {
    delete base.GroupingParameter;
  }

  return hasWrapperBase
    ? JSON.stringify({ ...wrapper, base: JSON.stringify(base) })
    : JSON.stringify(base);
}
